import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { NOTIFICATION_TYPE } from 'store/modules/notifications';

/**
 * Abstract class for {@code Notification} component.
 *
 * @augments Component
 */
export default class AbstractNotification extends PureComponent {
  /**
   * Default values for {@code Notification} component's properties.
   *
   * @static
   */
  static defaultProps = {
    appearance: NOTIFICATION_TYPE.NORMAL
  };

  /**
   * Initializes a new {@code Notification} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
  constructor(props) {
    super(props);

    // Bind event handler so it is only bound once for every instance.
    this._onDismissed = this._onDismissed.bind(this);
  }

  // _getDescription: () => Array<string>;

  /**
   * Returns the description array to be displayed.
   *
   * @protected
   * @returns {Array<string>}
   */
  _getDescription() {
    const { description, descriptionArguments, descriptionKey, t } = this.props;

    const descriptionArray = [];

    descriptionKey &&
      descriptionArray.push(t(descriptionKey, descriptionArguments));

    description && descriptionArray.push(description);

    return descriptionArray;
  }

  // _getDescriptionKey: () => string;

  /**
   * Returns the description key that was used if any.
   *
   * @protected
   * @returns {string}
   */
  _getDescriptionKey() {
    return this.props.descriptionKey;
  }

  // _onDismissed: () => void;

  /**
   * Callback to dismiss the notification.
   *
   * @private
   * @returns {void}
   */
  _onDismissed() {
    this.props.onDismissed(this.props.uid);
  }
}

AbstractNotification.propTypes = {
  /**
   * Display appearance for the component, passed directly to the
   * notification.
   */
  appearance: PropTypes.string,

  /**
   * Whether or not the title and description should be concatenated.
   */
  concatText: PropTypes.bool,

  /**
   * Callback invoked when the custom button is clicked.
   */
  customActionHandler: PropTypes.array,

  /**
   * The text to display as button in the notification for the custom action.
   */
  customActionNameKey: PropTypes.array,

  /**
   * The text to display in the body of the notification. If not passed
   * in, the passed in descriptionKey will be used.
   */
  defaultTitleKey: PropTypes.string,

  /**
   * A description string that can be used in addition to the prop
   * descriptionKey.
   */
  description: PropTypes.string,

  /**
   * The translation arguments that may be necessary for the description.
   */
  descriptionArguments: PropTypes.object,

  /**
   * The translation key to use as the body of the notification.
   */
  descriptionKey: PropTypes.string,

  /**
   * Whether the support link should be hidden in the case of an error
   * message.
   */
  hideErrorSupportLink: PropTypes.bool,

  /**
   * The type of icon to be displayed. If not passed in, the appearance
   * type will be used.
   */
  icon: PropTypes.bool,

  /**
   * Maximum lines of the description.
   */
  maxLines: PropTypes.number,

  /**
   * Callback invoked when the user clicks to dismiss the notification.
   */
  onDismissed: PropTypes.func,

  /**
   * Invoked to obtain translated strings.
   */
  t: PropTypes.func,

  /**
   * The text to display at the top of the notification. If not passed in,
   * the passed in titleKey will be used.
   */
  title: PropTypes.string,

  /**
   * The translation arguments that may be necessary for the title.
   */
  titleArguments: PropTypes.object,

  /**
   * The translation key to display as the title of the notification if
   * no title is provided.
   */
  titleKey: PropTypes.string,

  /**
   * The unique identifier for the notification.
   */
  uid: PropTypes.string
};
